/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { TrainingConfigVersion } from 'src/modules/modelTraining/types/trainingTypes';

export type ChangeTrainingConfigVersionModalData = {
    trainingConfigVersion: string;
};

export type ChangeTrainingConfigVersionModalProps = {
    trainingConfigVersions: TrainingConfigVersion[];
    initialTrainingConfigVersion: string;
};

const messages = defineMessages({
    modalTitle: {
        id: 'modelTraining.changeTrainingConfigVersionModal.changeTrainingConfigVersionModal.modalTitle',
        defaultMessage: 'Select version for model training '
    },
    modalSubmitBtn: {
        id: 'modelTraining.changeTrainingConfigVersionModal.changeTrainingConfigVersionModal.modalSubmitBtn',
        defaultMessage: 'Select'
    },
    configVersionPlaceholder: {
        id: 'modelTraining.changeTrainingConfigVersionModal.changeTrainingConfigVersionModal.selectModelPlaceholder',
        defaultMessage: 'Select training config version'
    },
    latestConfig: {
        id: 'modelTraining.changeTrainingConfigVersionModal.changeTrainingConfigVersionModal.latestConfig',
        defaultMessage: 'Latest'
    }
});

/**
 * Represents a modal to change the selected training config version.
 *
 * @param props The properties passed to the modal.
 */
export const ChangeTrainingConfigVersionModal = withModalMounter(
    (props: BasicModalProps<ChangeTrainingConfigVersionModalData> & ChangeTrainingConfigVersionModalProps) => {
        const { localize } = useLocalization();
        const { initialTrainingConfigVersion, trainingConfigVersions } = props;
        const [configVersion, setConfigVersion] = React.useState<string>(initialTrainingConfigVersion);

        const onSubmit = () => {
            props.onSubmit({ trainingConfigVersion: configVersion });
        };

        return (
            <BasicModal
                {...props}
                onSubmit={onSubmit}
                valid={Boolean(configVersion)}
                doneButtonText={localize(messages.modalSubmitBtn)}
                dialogContentProps={{ title: localize(messages.modalTitle) }}>
                <Stack styles={{ root: { padding: '2px' } }}>
                    <LuisDropdown
                        styles={{ root: { width: 320 } }}
                        selectedKey={configVersion}
                        placeholder={localize(messages.configVersionPlaceholder)}
                        onChange={(_, option) => setConfigVersion(option.key as string)}
                        options={trainingConfigVersions.map((config, _) => ({
                            key: config.trainingConfigVersion,
                            text: `${config.trainingConfigVersion}${config.isLatest === true ? ` (${localize(messages.latestConfig)})` : ''}`
                        }))}
                    />
                </Stack>
            </BasicModal>
        );
    }
);
